import { DeleteOutlined, DownloadOutlined, PaperClipOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Empty, Input, List, Modal, Space, Typography, Upload, message } from 'antd';
import type { UploadFile } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  attachmentAllowedExtensions,
  attachmentMaximumFileSizeBytes,
  attachmentMaximumFileSizeMegabytes,
  deleteProjectTaskAttachment,
  downloadProjectTaskAttachment,
  listProjectTaskAttachments,
  uploadProjectTaskAttachment,
} from '../../api/projects';
import type { ProjectTaskAttachment, ProjectTaskItem } from '../../api/projects';
import { formatDateTime } from './projectDisplay';

/** Số byte trong một kilobyte, dùng để đổi dung lượng sang đơn vị đọc được. */
const bytesPerKilobyte = 1024;

/** Đổi dung lượng byte sang chuỗi đọc được. */
function formatSize(sizeBytes: number): string {
  if (sizeBytes < bytesPerKilobyte) {
    return `${sizeBytes.toString()} B`;
  }

  const kilobytes = sizeBytes / bytesPerKilobyte;

  return kilobytes < bytesPerKilobyte
    ? `${kilobytes.toFixed(1)} KB`
    : `${(kilobytes / bytesPerKilobyte).toFixed(1)} MB`;
}

/**
 * Hộp thoại tệp đính kèm của một công việc.
 *
 * <b>Ảnh hiện trường là thứ được gửi nhiều nhất ở một công ty tích hợp hệ thống thị giác.</b> Một
 * tấm ảnh chụp góc lắp camera nói được nhiều hơn ba đoạn mô tả, và không có chỗ gửi trong hệ thống
 * thì nó nằm lại trong tin nhắn cá nhân — nơi người vào dự án sau không bao giờ tìm thấy.
 *
 * <b>Lọc trước ở đây chỉ để người dùng khỏi chờ vô ích, KHÔNG phải một lớp bảo vệ.</b> Máy chủ kiểm
 * lại toàn bộ: dung lượng, loại tệp, tên tệp. Bất cứ thứ gì trình duyệt gửi lên cũng có thể bị sửa.
 *
 * <b>Tải về đi qua lời gọi có kèm phiếu xác thực</b>, không đặt thẳng đường dẫn vào một thẻ liên
 * kết: điểm cuối đòi phiếu trong tiêu đề, mà thẻ liên kết thì không gửi tiêu đề nào.
 */
export function ProjectTaskAttachmentModal({
  projectId,
  task,
  open,
  onClose,
}: {
  readonly projectId: string;
  readonly task: ProjectTaskItem | null;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [attachments, setAttachments] = useState<readonly ProjectTaskAttachment[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || task === null) {
      return;
    }

    let isCurrent = true;

    async function load(taskId: string): Promise<void> {
      setErrorMessage(null);

      try {
        const loaded = await listProjectTaskAttachments(projectId, taskId);

        if (isCurrent) {
          setAttachments(loaded);
        }
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh sách tệp đính kèm.',
        );
      }
    }

    void load(task.id);

    return () => {
      isCurrent = false;
    };
  }, [projectId, task, open]);

  const reload = useCallback(async (): Promise<void> => {
    if (task === null) {
      return;
    }

    setAttachments(await listProjectTaskAttachments(projectId, task.id));
  }, [projectId, task]);

  async function upload(file: File): Promise<void> {
    if (task === null) {
      return;
    }

    setIsSaving(true);

    try {
      await uploadProjectTaskAttachment(projectId, task.id, file, description);

      setDescription('');
      await reload();
      message.success('Đã tải tệp lên.');
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không tải được tệp lên.');
    } finally {
      setIsSaving(false);
    }
  }

  async function download(attachment: ProjectTaskAttachment): Promise<void> {
    if (task === null) {
      return;
    }

    try {
      const blob = await downloadProjectTaskAttachment(projectId, task.id, attachment.id);

      /*
        Dựng một liên kết tạm rồi bấm nó bằng mã. Đây là cách duy nhất để trình duyệt lưu một khối
        nhị phân đã nằm trong bộ nhớ xuống đĩa với đúng tên tệp gốc.
      */
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = attachment.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Giải phóng ngay: mỗi khối giữ lại là một khoảng bộ nhớ không bao giờ được thu hồi.
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không tải được tệp về.');
    }
  }

  async function remove(attachment: ProjectTaskAttachment): Promise<void> {
    if (task === null) {
      return;
    }

    try {
      await deleteProjectTaskAttachment(projectId, task.id, attachment.id);

      await reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xóa được tệp.');
    }
  }

  /**
   * Kiểm một tệp trước khi gửi lên.
   *
   * Trả về false để chặn phép tải lên mặc định của thư viện thành phần — hệ thống này tự gửi tệp
   * bằng lời gọi có kèm phiếu xác thực, không dùng cơ chế tải lên sẵn có.
   */
  function checkBeforeUpload(file: File): boolean {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    if (!attachmentAllowedExtensions.includes(extension as never)) {
      message.error(
        `Loại tệp "${extension}" không được phép. Hệ thống chỉ nhận ảnh, tài liệu, bản vẽ và tệp nén.`,
      );

      return false;
    }

    if (file.size > attachmentMaximumFileSizeBytes) {
      message.error(
        `Tệp vượt quá ${attachmentMaximumFileSizeMegabytes.toString()} MB. Ảnh hiện trường hãy giảm kích thước trước khi gửi.`,
      );

      return false;
    }

    if (file.size === 0) {
      message.error('Tệp rỗng nên không tải lên được.');

      return false;
    }

    void upload(file);

    return false;
  }

  const items = attachments ?? [];

  return (
    <Modal
      open={open}
      title={task === null ? 'Tệp đính kèm' : `Tệp đính kèm — ${task.wbsCode} ${task.name}`}
      width={760}
      footer={null}
      onCancel={onClose}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

        <List<ProjectTaskAttachment>
          size="small"
          bordered
          dataSource={items as ProjectTaskAttachment[]}
          loading={attachments === null && errorMessage === null}
          locale={{
            emptyText: (
              <Empty description="Chưa có tệp nào. Một tấm ảnh hiện trường thường nói được nhiều hơn ba đoạn mô tả." />
            ),
          }}
          renderItem={(attachment) => (
            <List.Item
              actions={[
                <Button
                  key="download"
                  size="small"
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    void download(attachment);
                  }}
                />,
                ...(attachment.canDelete
                  ? [
                      <Button
                        key="delete"
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          void remove(attachment);
                        }}
                      />,
                    ]
                  : []),
              ]}
            >
              <List.Item.Meta
                avatar={<PaperClipOutlined />}
                title={
                  <Space size={8} wrap>
                    <Typography.Text strong>{attachment.fileName}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {formatSize(attachment.sizeBytes)}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDateTime(attachment.uploadedAt)}
                    </Typography.Text>
                  </Space>
                }
                description={
                  attachment.description === null ? null : (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {attachment.description}
                    </Typography.Text>
                  )
                }
              />
            </List.Item>
          )}
        />

        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Input
            value={description}
            placeholder="Mô tả ngắn, ví dụ: ảnh chụp góc lắp camera sau khi chỉnh"
            maxLength={500}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
          <Upload
            beforeUpload={checkBeforeUpload}
            showUploadList={false}
            accept={attachmentAllowedExtensions.join(',')}
            fileList={[] as UploadFile[]}
          >
            <Button type="primary" icon={<UploadOutlined />} loading={isSaving}>
              Chọn tệp để tải lên
            </Button>
          </Upload>
        </Space>

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Tối đa {attachmentMaximumFileSizeMegabytes.toString()} MB mỗi tệp. Nhận ảnh, tài liệu, bản
          vẽ và tệp nén — tệp chạy được thì không, vì một tệp độc hại tải lên được sẽ phát tán cho cả
          công ty.
        </Typography.Text>
      </Space>
    </Modal>
  );
}
